/* eslint-disable react-refresh/only-export-components */
import { useId, useRef, useState, type ReactNode } from 'react'
import {
  CheckCircle2,
  Info,
  X,
} from 'lucide-react'
import type {
  AwaStatus,
  CaseReviewState,
  DocumentSelectOptionGroup,
  ReadinessCheck,
  SourceDocumentRecord,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'

export function ReadinessChecklist({ checks }: { checks: ReadinessCheck[] }) {
  return (
    <section className="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Review readiness checklist</h3>
        <InlineHelp text="Use this checklist for the review gate only. It summarizes what still blocks or warns on case readiness." />
      </div>
      {checks.length === 0 && (
        <div className="mt-3 rounded border border-dashed border-neutral-300 bg-neutral-50 p-3 text-sm text-neutral-600">
          Readiness details are still loading for this case.
        </div>
      )}
      <ul className="mt-3 space-y-2">
        {checks.map((check) => (
          <li key={check.id} className="rounded-lg border border-neutral-200 p-2.5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-primary">{check.label}</div>
                <p className="mt-0.5 text-sm text-neutral-600">{check.detail}</p>
              </div>
              <StatusPill label={check.status} tone={toneForReadiness(check.status)} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function ReadinessOverviewCard({
  title,
  description,
  complete,
  total,
  summaryTiles = [],
}: {
  title: string
  description: string
  complete: number
  total: number
  summaryTiles?: Array<{ label: string; value: string; icon: ReactNode }>
}) {
  return (
    <TrackerWorkspacePanel
      eyebrow={title}
      title="Case document workspace"
      helpText={description}
      summaryTiles={[
        {
          label: 'Required records complete',
          value: `${complete}/${total}`,
          icon: <CheckCircle2 size={18} aria-hidden="true" />,
        },
        ...summaryTiles,
      ]}
    />
  )
}

export function TrackerWorkspacePanel({
  eyebrow,
  title,
  helpText,
  action,
  body,
  summaryTiles,
}: {
  eyebrow: string
  title: string
  helpText?: string
  action?: ReactNode
  body?: ReactNode
  summaryTiles: Array<{ label: string; value: string; icon: ReactNode }>
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-[#12335a] bg-[radial-gradient(circle_at_top_left,_rgba(37,86,145,0.35),_transparent_36%),linear-gradient(180deg,_#071a33,_#0b2547_48%,_#08172c)] shadow-[0_24px_60px_rgba(4,16,35,0.16)]">
      <div className="flex flex-col gap-4 border-b border-white/10 px-6 py-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#89a7cb]">{eyebrow}</div>
            {helpText ? <InlineHelp title={eyebrow} text={helpText} light /> : null}
          </div>
          <h3 className="mt-2 text-[1.7rem] font-bold uppercase tracking-[0.08em] text-white">{title}</h3>
        </div>
        {action ? <div className="flex flex-wrap items-center gap-3">{action}</div> : null}
      </div>
      {body ? <div className="px-6 py-5">{body}</div> : null}
      {summaryTiles.length > 0 ? (
        <div className={`grid gap-3 px-6 py-5 ${
          summaryTiles.length <= 2
            ? 'md:grid-cols-2'
            : summaryTiles.length === 3
              ? 'md:grid-cols-3'
              : 'md:grid-cols-4'
        }`}>
          {summaryTiles.map((tile) => (
            <TrackerSummaryTile key={tile.label} label={tile.label} value={tile.value} icon={tile.icon} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

function TrackerSummaryTile({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
      <div className="flex items-center gap-2 text-[#9fb4cf]">
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-[0.24em]">{label}</span>
      </div>
      <div className="mt-2 text-base font-semibold text-white">{value}</div>
    </div>
  )
}

export function SummaryTile({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5">
      <div className="flex items-center gap-2 text-neutral-500">
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <div className="mt-1.5 text-base font-bold text-primary">{value}</div>
    </div>
  )
}

function isoDateToUsDate(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return ''
  const [, year, month, day] = match
  return `${month}/${day}/${year}`
}

function usDateToIsoDate(value: string) {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return ''
  const [, month, day, year] = match
  return `${year}-${month}-${day}`
}

function formatUsDateInput(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 8)

  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

function formatCurrencyInput(value: string) {
  const sanitized = value.replace(/[^0-9.-]/g, '')
  if (!sanitized || sanitized === '-' || sanitized === '.' || sanitized === '-.') return ''
  const numericValue = Number(sanitized)
  if (!Number.isFinite(numericValue)) return ''
  return numericValue.toFixed(2)
}

export function FieldControl({
  fieldType,
  value,
  options,
  placeholder,
  disabled = false,
  onChange,
  onBlur,
}: {
  fieldType: 'text' | 'number' | 'date' | 'textarea' | 'select' | 'currency'
  value: string
  options?: Array<{ value: string; label: string } | DocumentSelectOptionGroup>
  placeholder?: string
  disabled?: boolean
  onChange: (next: string) => void
  onBlur?: () => void
}) {
  const nativeDateInputRef = useRef<HTMLInputElement | null>(null)
  const baseClassName = `mt-2 w-full rounded-md border px-3 py-2 text-sm ${
    disabled
      ? 'border-neutral-200 bg-neutral-100 text-neutral-500'
      : 'border-neutral-300 text-neutral-900'
  }`

  function openNativeDatePicker() {
    if (disabled) return
    nativeDateInputRef.current?.showPicker?.()
  }

  if (fieldType === 'textarea') {
    return (
      <textarea
        rows={4}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        className={baseClassName}
      />
    )
  }

  if (fieldType === 'select') {
    return (
      <select value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} onBlur={onBlur} className={baseClassName}>
        <option value="">{placeholder ?? 'Select a value'}</option>
        {options?.map((option) =>
          'options' in option ? (
            <optgroup key={option.label} label={option.label}>
              {option.options.map((groupOption) => (
                <option key={groupOption.value} value={groupOption.value}>
                  {groupOption.label}
                </option>
              ))}
            </optgroup>
          ) : (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ),
        )}
      </select>
    )
  }

  if (fieldType === 'date') {
    return (
      <div className="mt-2">
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          inputMode="numeric"
          autoComplete="off"
          onFocus={openNativeDatePicker}
          onClick={openNativeDatePicker}
          onChange={(event) => {
            onChange(formatUsDateInput(event.target.value))
            openNativeDatePicker()
          }}
          onBlur={onBlur}
          className={`w-full rounded-md border px-3 py-2 text-sm ${
            disabled
              ? 'border-neutral-200 bg-neutral-100 text-neutral-500'
              : 'border-neutral-300 text-neutral-900'
          }`}
        />
        <input
          ref={nativeDateInputRef}
          aria-label="Native date picker"
          tabIndex={-1}
          type="date"
          value={usDateToIsoDate(value)}
          onChange={(event) => {
            onChange(isoDateToUsDate(event.target.value))
            onBlur?.()
          }}
          className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
        />
      </div>
    )
  }

  const inputType =
    fieldType === 'currency' || fieldType === 'number'
      ? 'text'
      : fieldType
  const inputMode =
    fieldType === 'number' || fieldType === 'currency'
        ? 'decimal'
        : undefined

  if (fieldType === 'currency') {
    return (
      <div className="mt-2 relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-neutral-500">
          $
        </span>
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          inputMode="decimal"
          onChange={(event) => onChange(event.target.value.replace(/\$/g, ''))}
          onBlur={() => {
            onChange(formatCurrencyInput(value))
            onBlur?.()
          }}
          className={`${baseClassName} pl-7`}
        />
      </div>
    )
  }

  return (
    <input
      type={inputType}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      inputMode={inputMode}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      className={baseClassName}
    />
  )
}

export function MetadataItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
      <dt className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-1.5 text-sm font-medium text-primary">{value}</dd>
    </div>
  )
}

export function OptionalSectionNote({
  label = 'Section Review',
  value,
  placeholder,
  onChange,
  onBlur,
  actions,
}: {
  label?: string
  value: string
  placeholder: string
  onChange: (next: string) => void
  onBlur?: () => void
  actions?: ReactNode
}) {
  const [open, setOpen] = useState(Boolean(value))

  return (
    <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
      {open && (
        <textarea
          rows={2}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          className="mt-3 w-full rounded border border-neutral-300 px-3 py-2 text-sm text-neutral-900"
        />
      )}
      <div className="mt-3 flex justify-end gap-2">
        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:border-secondary hover:text-secondary"
          >
            Add Comment
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded border border-neutral-300 px-2.5 py-1 text-xs font-medium text-neutral-700 hover:border-secondary hover:text-secondary"
          >
            Hide Comment
          </button>
        )}
        {actions}
      </div>
    </div>
  )
}

export function StatusPill({ label, tone }: { label: string; tone: 'neutral' | 'success' | 'warning' | 'error' | 'info' }) {
  const toneClassName =
    tone === 'success'
      ? 'bg-success/10 text-success'
      : tone === 'warning'
        ? 'bg-warning/10 text-warning'
        : tone === 'error'
          ? 'bg-error/10 text-error'
          : tone === 'info'
            ? 'bg-secondary-lightest text-secondary'
            : 'bg-neutral-100 text-neutral-600'

  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${toneClassName}`}>{label}</span>
}

export function InlineHelp({
  text,
  title = 'Help',
  align = 'right',
  light = false,
}: {
  text: string
  title?: string
  align?: 'left' | 'right'
  light?: boolean
}) {
  const [open, setOpen] = useState(false)
  const id = useId()

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        aria-label={title}
        title={title}
        onClick={() => setOpen((current) => !current)}
        onBlur={(event) => {
          if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node | null)) {
            setOpen(false)
          }
        }}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
          light
            ? 'border border-white/20 text-[#9fb4cf] hover:border-white/40 hover:text-white'
            : 'border border-neutral-300 text-neutral-500 hover:border-secondary hover:text-secondary'
        }`}
      >
        <Info size={12} aria-hidden="true" />
      </button>
      {open && (
        <div
          id={`${id}-panel`}
          role="dialog"
          aria-label={title}
          className={`absolute top-7 z-20 w-72 rounded-lg border border-neutral-200 bg-white p-3 text-sm text-neutral-700 shadow-lg ${
            align === 'left' ? 'left-0' : 'right-0'
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-700">{title}</div>
              <p className="mt-1 whitespace-pre-wrap">{text}</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
              aria-label="Close help"
            >
              <X size={12} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </span>
  )
}

export function SectionHeading({
  eyebrow,
  title,
  helpText,
  action,
}: {
  eyebrow?: string
  title: string
  helpText?: string
  action?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {eyebrow && eyebrow !== title && (
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{eyebrow}</div>
        )}
        <div className="mt-1 flex items-center gap-2">
          <h3 className="text-base font-bold text-primary">{title}</h3>
          {helpText && <InlineHelp text={helpText} title={`${title} help`} />}
        </div>
      </div>
      {action}
    </div>
  )
}

export function toneForDocumentStatus(status: StructuredDocumentRecord['status'] | SourceDocumentRecord['status']) {
  switch (status) {
    case 'complete':
    case 'ready':
      return 'success'
    case 'needs_attention':
      return 'error'
    case 'in_progress':
      return 'info'
    case 'missing':
      return 'warning'
    default:
      return 'neutral'
  }
}

export function toneForReadiness(status: ReadinessCheck['status']) {
  switch (status) {
    case 'complete':
      return 'success'
    case 'warning':
      return 'warning'
    default:
      return 'error'
  }
}

export function toneForAwaStatus(status: AwaStatus) {
  switch (status) {
    case 'completed':
      return 'success'
    case 'needs_attention':
      return 'error'
    case 'in_progress':
      return 'info'
    default:
      return 'warning'
  }
}

export function reviewReadinessLabel(status: CaseReviewState['readinessStatus']) {
  switch (status) {
    case 'missing_required_documents':
      return 'Missing Required Documents'
    case 'incomplete_documents':
      return 'Incomplete Documents'
    case 'ready_with_warnings':
      return 'Ready with Warnings'
    case 'ready_for_review':
      return 'Ready for Review'
    case 'review_in_progress':
      return 'Review In Progress'
    case 'review_completed':
      return 'Review Completed'
  }
}

export function toneForReviewReadinessStatus(status: CaseReviewState['readinessStatus']) {
  switch (status) {
    case 'ready_for_review':
    case 'review_completed':
      return 'success'
    case 'ready_with_warnings':
    case 'review_in_progress':
      return 'info'
    case 'incomplete_documents':
      return 'warning'
    default:
      return 'error'
  }
}

export function reviewCompletenessLabel(status: CaseReviewState['documentSummaries'][number]['completenessStatus']) {
  switch (status) {
    case 'not_started':
      return 'Not Started'
    case 'uploaded':
      return 'Uploaded'
    case 'auto_populated':
      return 'Auto Populated'
    case 'in_progress':
      return 'In Progress'
    case 'incomplete':
      return 'Incomplete'
    case 'complete':
      return 'Complete'
    case 'reviewed':
      return 'Reviewed'
  }
}

export function toneForDocumentCompleteness(status: CaseReviewState['documentSummaries'][number]['completenessStatus']) {
  switch (status) {
    case 'complete':
    case 'reviewed':
      return 'success'
    case 'auto_populated':
    case 'uploaded':
    case 'in_progress':
      return 'info'
    case 'incomplete':
      return 'warning'
    default:
      return 'neutral'
  }
}

export function toneForSeverity(severity: CaseReviewState['findings'][number]['severity']) {
  switch (severity) {
    case 'blocks_acceptance':
      return 'error'
    case 'high':
      return 'error'
    case 'moderate':
      return 'warning'
    case 'low':
      return 'info'
    default:
      return 'neutral'
  }
}

export function findingSeverityLabel(severity: CaseReviewState['findings'][number]['severity']) {
  switch (severity) {
    case 'informational':
      return 'Informational'
    case 'low':
      return 'Low Risk'
    case 'moderate':
      return 'Moderate Risk'
    case 'high':
      return 'High Risk'
    case 'blocks_acceptance':
      return 'Blocks Acceptance / Transmit'
  }
}

export function toneForFindingStatus(status: CaseReviewState['findings'][number]['status']) {
  switch (status) {
    case 'resolved':
      return 'success'
    case 'investigating':
      return 'info'
    default:
      return 'warning'
  }
}

export function toneForRecommendationState(state: StructuredDocumentRecord['awaRecommendations'][number]['state']) {
  switch (state) {
    case 'resolved':
      return 'success'
    case 'partially_applied':
      return 'info'
    case 'dismissed':
      return 'neutral'
    default:
      return 'warning'
  }
}

export function toneForSourceAutomation(status: SourceDocumentRecord['autoPopulationStatus']) {
  switch (status) {
    case 'applied':
      return 'success'
    case 'conflict':
      return 'warning'
    case 'needs_review':
      return 'error'
    case 'pending':
      return 'info'
    default:
      return 'neutral'
  }
}
